/* eslint-disable max-lines */
/**
 * UI actions:
 * Store selector,
 * Sidebar actions,
 * Loader actions
 */
import { NgRedux } from '@angular-redux/store';
import { Injectable } from '@angular/core';
import { rx } from '@shared/rx';
import { IAppState } from '@store/reducers';
import {
  EDITOR_MODE,
  IAction,
  IDropdownMenuOptions,
  IEntityItemRequestOptions,
  IErrorPageType,
  IMenuItem,
  IModalDialogOptions,
  IModalDialogResult,
  INotificationBoxItem,
  INotificationToaster,
  IOpenRouteOptions,
  IOpenRouteWithFilterModelType,
  ISite,
  IUserConfig,
  NotificationType,
  PromptSiteSelectorOptions,
  ToolbarButtonDefinition,
} from '@shared/types';

import { Toast } from 'angular2-toaster';
import { generateId, generateObjectId } from '@shared/utils';
import {
  BUTTON_TEMPLATES,
  ERROR_PAGE_TEMPLATES,
  MODAL_TEMPLATES,
  TOASTER_MESSAGES,
} from '@app/text-content';
import {
  filter,
  get,
  has,
  head,
  isEmpty,
  isEqual,
  isString,
  merge,
  set,
} from 'lodash';
import { IProgressBarLoader, IUIListState } from '@store/reducers/ui';
import { STATE_KEYS } from '@shared/services/state-service/state-selectors';
import { DropdownMenuService } from '@geniuspos/ui-shared/modules/xdm-shared-service/dropdown-menu/dropdown-menu.service';
import { PdfSelectColumn } from '@app/shared/common/make-pdf/v2';
import { XVII18nService } from '@app/i18n/services';

@Injectable()
export class UiActions {
  // site selector modal
  public static SET_FORM_MODE: string = 'SET_FORM_MODE';
  public static SELECT_SIDEBAR_ITEM: string = 'SELECT_SIDEBAR_ITEM';
  public static UPDATE_LOADER: string = 'UPDATE_LOADER';
  public static UPDATE_MODAL_LOADER: string = 'UPDATE_MODAL_LOADER';
  public static SHOW_GENERIC_MODAL: string = 'SHOW_GENERIC_MODAL';
  public static HIDE_GENERIC_MODAL: string = 'HIDE_GENERIC_MODAL';
  public static SHOW_FORM_MODAL: string = 'SHOW_FORM_MODAL';
  public static HIDE_FORM_MODAL: string = 'HIDE_FORM_MODAL';
  public static STORE_VALUE_BY_MODAL_ID: string = 'STORE_VALUE_BY_MODAL_ID';
  public static SHOW_DROPDOWN_MENU: string = 'SHOW_DROPDOWN_MENU';
  public static HIDE_DROPDOWN_MENU: string = 'HIDE_DROPDOWN_MENU';

  public static GENERIC_SUCCESS_ACTION: string = 'GENERIC_SUCCESS_ACTION';
  public static GENERIC_ERROR_ACTION: string = 'GENERIC_ERROR_ACTION';
  public static GENERIC_TOASTER_ACTION: string = 'GENERIC_TOASTER_ACTION';

  public static SHOW_ERROR_PAGE_ACTION: string = 'SHOW_ERROR_PAGE_ACTION';
  public static UPDATE_SIDEBAR_ITEMS: string = 'UPDATE_SIDEBAR_ITEMS';

  public static STORE_ENTITY_LIST_FILTER_TEXT: string =
    'STORE_ENTITY_LIST_FILTER_TEXT';

  // list view
  public static RESTORE_ENTITY_LIST_VIEW: string = 'RESTORE_ENTITY_LIST_VIEW';
  public static RESTORE_DEFAULT_LIST_VIEW: string = 'RESTORE_DEFAULT_LIST_VIEW';
  public static RESTORE_USER_LIST_VIEW: string = 'RESTORE_USER_LIST_VIEW';
  public static SAVE_USER_LIST_VIEW: string = 'SAVE_USER_LIST_VIEW';
  public static REMOVE_USER_LIST_VIEW: string = 'REMOVE_USER_LIST_VIEW';
  public static UPDATE_LIST_VIEW_DATA: string = 'UPDATE_LIST_VIEW_DATA';
  public static UPDATE_DEFAULT_LIST_VIEW_DATA: string =
    'UPDATE_DEFAULT_LIST_VIEW_DATA';
  public static SET_CURRENT_PAGE: string = 'SET_CURRENT_PAGE';

  public static ENTITY_ITEM_REQUEST_PENDING: string =
    'ENTITY_ITEM_REQUEST_PENDING';

  public static SET_UI_THEME: string = 'SET_UI_THEME';

  // navbar actions
  public static SHOW_NAVBAR: string = 'SHOW_NAVBAR';
  public static HIDE_NAVBAR: string = 'HIDE_NAVBAR';
  public static HIDE_MENUITEMS: string = 'HIDE_MENUITEMS';
  public static SET_SIDEBAR_ITEM_OPEN_STATE: string =
    'SET_SIDEBAR_ITEM_OPEN_STATE';

  // reload actions
  public static OPEN_ROUTE_ACTION: string = 'OPEN_ROUTE_ACTION';

  public static SET_UI_FEATURES: string = 'SET_UI_FEATURES';

  // form building actions
  public static FORM_BUILDING_STARTED: string = 'FORM_BUILDING_STARTED';
  public static FORM_BUILDING_COMPLETE: string = 'FORM_BUILDING_COMPLETE';
  public static SAVE_NON_PATCHED_FORM_VALUE: string =
    'SAVE_NON_PATCHED_FORM_VALUE';
  public static CLEAR_NON_PATCHED_FORM_VALUE: string =
    'CLEAR_NON_PATCHED_FORM_VALUE';

  public static ADD_NOTIFICATION_TO_THE_BOX: string =
    'ADD_NOTIFICATION_TO_THE_BOX';
  public static REMOVE_NOTIFICATION_FROM_THE_BOX: string =
    'REMOVE_NOTIFICATION_FROM_THE_BOX';
  public static REMOVE_ALL_NOTIFICATION_FROM_THE_BOX: string =
    'REMOVE_ALL_NOTIFICATION_FROM_THE_BOX';
  public static REMOVE_NOTIFICATION_ON_PAGE_LEAVE_FROM_THE_BOX: string =
    'REMOVE_NOTIFICATION_ON_PAGE_LEAVE_FROM_THE_BOX';

  public static UPDATE_DOCUMENT_VALUES: string = 'UPDATE_DOCUMENT_VALUES';

  public static SET_GRID_ROW_LOADER: string = 'SET_GRID_ROW_LOADER';

  public static SET_IS_DELETED_VIEW_MODE: string = 'SET_IS_DELETED_VIEW_MODE';

  public static KEEP_BATCH_REQUEST_LOADING: string =
    'KEEP_BATCH_REQUEST_LOADING';

  public static RELOAD_LIST_CONFIG: string = 'RELOAD_LIST_CONFIG';

  public static UPDATE_PREVENT_DISABLE_LOADER_STATUS: string =
    'UPDATE_PREVENT_DISABLE_LOADER_STATUS';

  public static FETCH_USER_CONFIG: string = 'FETCH_USER_CONFIG';

  public static FETCH_USER_CONFIG_SUCCESS: string = 'FETCH_USER_CONFIG_SUCCESS';

  public static FETCH_USER_CONFIG_ERROR: string = 'FETCH_USER_CONFIG_ERROR';

  public static UPDATE_USER_CONFIG: string = 'UPDATE_USER_CONFIG';

  // PDF print actions
  public static SAVE_PRINT_COLUMNS: string = 'SAVE_PRINT_COLUMNS';

  public static CART_CHANGES_VALIDATED_AND_APPLIED_ERROR: string =
    'CART_CHANGES_VALIDATED_AND_APPLIED_ERROR';

  public static UPDATE_BREADCRUMB_BUTTONS = 'UPDATE_BREADCRUMB_BUTTONS';
  public static CLEAR_DUPLICATE_OPTION = 'CLEAR_DUPLICATE_OPTION';

  public static setIsDeleteViewMode(flag: boolean): IAction {
    return {
      type: UiActions.SET_IS_DELETED_VIEW_MODE,
      payload: { flag },
    };
  }

  /**
   * Apply a theme (class) to the body container.
   */
  public static setUiThemeAction(theme = ''): IAction {
    return {
      type: UiActions.SET_UI_THEME,
      payload: { theme },
    };
  }

  /**
   *  Show navbar action
   */
  public static showNavbarAction(): IAction {
    return {
      type: UiActions.SHOW_NAVBAR,
    };
  }

  public static setPendingRequestStatusAction(
    options: IEntityItemRequestOptions,
  ): IAction {
    return {
      type: UiActions.ENTITY_ITEM_REQUEST_PENDING,
      payload: options,
    };
  }

  /**
   *  Hide navbar action
   */
  public static hideNavbarAction(): IAction {
    return {
      type: UiActions.HIDE_NAVBAR,
    };
  }

  /**
   *  Hide menu items action
   */
  public static hideMenuItemsAction(hidden: boolean): IAction {
    return {
      type: UiActions.HIDE_MENUITEMS,
      payload: { hidden },
    };
  }

  /**
   *  save Print Columns action
   */
  public static savePrintColumnsAction(
    printCols: Record<string, PdfSelectColumn[]>,
  ): IAction {
    return {
      type: UiActions.SAVE_PRINT_COLUMNS,
      payload: printCols,
    };
  }

  /**
   * Set form mode to storage
   */
  public static setFormModeAction(formMode: EDITOR_MODE): IAction {
    return {
      type: UiActions.SET_FORM_MODE,
      payload: { formMode },
    };
  }

  /**
   * Select sidebar item action
   */
  public static selectSidebarItemAction(
    selectedItem: Partial<IMenuItem>,
  ): IAction {
    return {
      type: UiActions.SELECT_SIDEBAR_ITEM,
      payload: selectedItem,
    };
  }

  /**
   * Update loader action
   */
  public static updateLoaderAction(payload: {
    loader: boolean;
    progressBarLoader?: Partial<IProgressBarLoader>;
  }): IAction {
    return {
      type: UiActions.UPDATE_LOADER,
      payload: {
        loader: payload.loader,
        progressBarLoader: payload.progressBarLoader,
      },
    };
  }

  /**
   * Update loader action
   */
  public static updateModalLoaderAction(modalLoader: boolean): IAction {
    return {
      type: UiActions.UPDATE_MODAL_LOADER,
      payload: { modalLoader },
    };
  }

  /**
   * show generic modal action
   */
  public static showGenericModalDialogAction(payload): IAction {
    return {
      payload,
      type: UiActions.SHOW_GENERIC_MODAL,
    };
  }

  /**
   * hide generic modal action
   */
  public static hideGenericModalDialogAction(payload): IAction {
    return {
      payload,
      type: UiActions.HIDE_GENERIC_MODAL,
    };
  }

  /**
   * show form modal action
   */
  public static showFormModalAction(payload: IModalDialogOptions): IAction {
    return {
      payload,
      type: UiActions.SHOW_FORM_MODAL,
    };
  }

  /**
   * hide from modal action
   */
  public static hideFormModalAction(payload?: IModalDialogResult): IAction {
    return {
      payload,
      type: UiActions.HIDE_FORM_MODAL,
    };
  }

  public static storeValueByModalId(
    modalId: string | number,
    value: any,
  ): IAction {
    return {
      payload: {
        modalId,
        value,
      },
      type: UiActions.STORE_VALUE_BY_MODAL_ID,
    };
  }

  public static showDropdownMenuAction(payload): IAction {
    return {
      payload,
      type: UiActions.SHOW_DROPDOWN_MENU,
    };
  }

  public static hideDropdownMenuAction(payload): IAction {
    return {
      payload,
      type: UiActions.HIDE_DROPDOWN_MENU,
    };
  }

  public static showErrorToasterMessageAction(payload): IAction {
    return {
      payload,
      type: UiActions.GENERIC_ERROR_ACTION,
    };
  }

  public static showToasterMessageAction(payload): IAction {
    return {
      payload,
      type: UiActions.GENERIC_TOASTER_ACTION,
    };
  }

  public static showErrorPageAction(errorPage): IAction {
    return {
      payload: { errorPage },
      type: UiActions.SHOW_ERROR_PAGE_ACTION,
    };
  }

  /**
   * Actions for force navigate
   *
   * @param uri: string
   * @param options: object
   * @returns
   */
  public static openRouteAction(uri, options?): IAction {
    return {
      payload: {
        uri,
        options,
      },
      type: UiActions.OPEN_ROUTE_ACTION,
    };
  }

  public static clearDuplicateOption(): IAction {
    return {
      type: UiActions.CLEAR_DUPLICATE_OPTION,
    };
  }

  public static updateSidebarItemsAction(items): IAction {
    return {
      payload: { items },
      type: UiActions.UPDATE_SIDEBAR_ITEMS,
    };
  }

  /**
   * Actions for showing loader on form ready
   *
   * @returns
   */
  public static formBuildingStartedAction(options = {}): IAction {
    return {
      type: UiActions.FORM_BUILDING_STARTED,
      payload: { options },
    };
  }

  public static formBuildingCompleteAction(options = {}): IAction {
    return {
      type: UiActions.FORM_BUILDING_COMPLETE,
      payload: { options },
    };
  }

  public static saveNonPatchedFormValueAction(formValue): IAction {
    return {
      type: UiActions.SAVE_NON_PATCHED_FORM_VALUE,
      payload: formValue,
    };
  }

  public static clearNonPatchedFormValueAction(): IAction {
    return {
      type: UiActions.CLEAR_NON_PATCHED_FORM_VALUE,
      payload: null,
    };
  }

  /**
   * Actions for update listFilterText
   *
   * @param entityType: string
   * @param text: string
   * @returns
   */
  public static storeEntityListFilterTextAction(
    entityType: string,
    text: string,
  ): IAction {
    return {
      payload: {
        text,
        entity_type: entityType,
      },
      type: UiActions.STORE_ENTITY_LIST_FILTER_TEXT,
    };
  }

  /**
   * Actions for update list view data
   */
  public static restoreEntityListViewAction(entityType: string): IAction {
    return {
      payload: {
        entity_type: entityType,
      },
      type: UiActions.RESTORE_ENTITY_LIST_VIEW,
    };
  }

  /**
   * Actions for update list view data
   */
  public static restoreDefaultListViewAction(entityType: string): IAction {
    return {
      payload: {
        entity_type: entityType,
        viewName: 'View',
      },
      type: UiActions.RESTORE_DEFAULT_LIST_VIEW,
    };
  }

  public static saveUserListViewAction(viewName: string): IAction {
    return {
      payload: {
        viewName,
      },
      type: UiActions.SAVE_USER_LIST_VIEW,
    };
  }

  public static removeUserListViewAction(viewName: string): IAction {
    return {
      payload: {
        viewName,
      },
      type: UiActions.REMOVE_USER_LIST_VIEW,
    };
  }

  public static restoreUserListViewAction(viewName: string): IAction {
    return {
      payload: {
        viewName,
      },
      type: UiActions.RESTORE_USER_LIST_VIEW,
    };
  }

  public static updateListViewDataAction(payload: IUIListState): IAction {
    return {
      payload,
      type: UiActions.UPDATE_LIST_VIEW_DATA,
    };
  }

  public static updateDefaultListViewDataAction(
    payload: IUIListState,
  ): IAction {
    return {
      payload,
      type: UiActions.UPDATE_DEFAULT_LIST_VIEW_DATA,
    };
  }

  public static setCurrentPageAction(pageNumber: number): IAction {
    return {
      payload: {
        pageNumber,
      },
      type: UiActions.SET_CURRENT_PAGE,
    };
  }

  public static setSidebarOpenStateAction(
    item: Partial<IMenuItem>,
    openStatus: boolean,
  ): IAction {
    return {
      payload: { item, openStatus },
      type: UiActions.SET_SIDEBAR_ITEM_OPEN_STATE,
    };
  }

  public static setUiFeaturesAction(payload: {
    [key: string]: boolean;
  }): IAction {
    return {
      payload,
      type: UiActions.SET_UI_FEATURES,
    };
  }

  public static addNotificationAction(
    notificationItem: INotificationBoxItem,
  ): IAction {
    return {
      payload: notificationItem,
      type: UiActions.ADD_NOTIFICATION_TO_THE_BOX,
    };
  }

  public static removeNotificationAction(notificationId: string): IAction {
    return {
      payload: {
        notificationId,
      },
      type: UiActions.REMOVE_NOTIFICATION_FROM_THE_BOX,
    };
  }

  public static removeAllNotificationAction(): IAction {
    return {
      type: UiActions.REMOVE_ALL_NOTIFICATION_FROM_THE_BOX,
    };
  }

  public static removeNotificationOnPageLeave(): IAction {
    return {
      type: UiActions.REMOVE_NOTIFICATION_ON_PAGE_LEAVE_FROM_THE_BOX,
    };
  }

  public static setDocumentValuesChangesAction(items): IAction {
    return {
      payload: items,
      type: UiActions.UPDATE_DOCUMENT_VALUES,
    };
  }

  public static setGridRowLoaderAction(isActiveState: boolean): IAction {
    return {
      type: UiActions.SET_GRID_ROW_LOADER,
      payload: {
        gridRowLoader: isActiveState,
      },
    };
  }

  public static setBatchRequestLoadingStatus(isLoading: boolean): IAction {
    return {
      type: UiActions.KEEP_BATCH_REQUEST_LOADING,
      payload: {
        isBatchRequestLoading: isLoading,
      },
    };
  }

  public static reloadListConfig(changesNumber: number): IAction {
    return {
      type: UiActions.RELOAD_LIST_CONFIG,
      payload: {
        reloadListConfig: changesNumber,
      },
    };
  }

  public static updatePreventDisableLoaderStatus(
    isPreventLoaderCloseAfterRequestFinish: boolean,
  ): IAction {
    return {
      type: UiActions.UPDATE_PREVENT_DISABLE_LOADER_STATUS,
      payload: {
        isPreventLoaderCloseAfterRequestFinish,
      },
    };
  }

  public static fetchUserConfigAction(): IAction {
    return {
      type: UiActions.FETCH_USER_CONFIG,
    };
  }

  public static fetchUserConfigSuccessAction(userConfig): IAction {
    return {
      type: UiActions.FETCH_USER_CONFIG_SUCCESS,
      payload: {
        userConfig,
      },
    };
  }

  public static fetchUserConfigErrorAction(): IAction {
    return {
      type: UiActions.FETCH_USER_CONFIG_ERROR,
    };
  }

  public static updateUserConfigAction(userConfig: IUserConfig[]): IAction {
    return {
      type: UiActions.UPDATE_USER_CONFIG,
      payload: {
        userConfig,
      },
    };
  }

  public static showCartErrorToasterMessageAction(
    payload: INotificationToaster,
  ): IAction {
    return {
      type: UiActions.CART_CHANGES_VALIDATED_AND_APPLIED_ERROR,
      payload: {
        ...payload,
        type: NotificationType.ERROR,
      },
    };
  }

  public static updateBreadcrumbButtons(
    payload: ToolbarButtonDefinition[],
  ): IAction {
    return {
      payload,
      type: UiActions.UPDATE_BREADCRUMB_BUTTONS,
    };
  }

  public constructor(
    private ngRedux: NgRedux<IAppState>,
    private dropDownMenuService: DropdownMenuService,
    private i18nService: XVII18nService,
  ) {}

  /**
   * Dispatch show navbar action
   */
  public showNavbar(): void {
    this.ngRedux.dispatch(UiActions.showNavbarAction());
  }

  public setPendingRequestStatus(
    pendingRequestOptions: IEntityItemRequestOptions,
  ): void {
    this.ngRedux.dispatch(
      UiActions.setPendingRequestStatusAction(pendingRequestOptions),
    );
  }

  /**
   * Dispatch hide navbar action
   */
  public hideNavbar(): void {
    this.ngRedux.dispatch(UiActions.hideNavbarAction());
  }

  /**
   * Dispatch hideMenuItems action
   */
  public hideMenuItems(hidden: boolean): void {
    this.ngRedux.dispatch(UiActions.hideMenuItemsAction(hidden));
  }

  /**
   * Dispatch savePrintColumns action
   */
  public savePrintColumns(printCols: Record<string, PdfSelectColumn[]>): void {
    this.ngRedux.dispatch(UiActions.savePrintColumnsAction(printCols));
  }

  /**
   * Dispatch set form mode action
   */
  public setFormMode(formMode: EDITOR_MODE): void {
    this.ngRedux.dispatch(UiActions.setFormModeAction(formMode));
  }

  /**
   * Dispatch selected sidebar item action
   */
  public selectSidebarItem(item: Partial<IMenuItem>): void {
    this.ngRedux.dispatch(UiActions.selectSidebarItemAction(item));
  }

  /**
   * Dispatch action for enabling loader
   *
   */
  public enableLoader(
    progressBarLoader: Partial<IProgressBarLoader> = null,
  ): void {
    this.ngRedux.dispatch(
      UiActions.updateLoaderAction({ progressBarLoader, loader: true }),
    );
  }

  /**
   * Dispatch action for disabling loader
   */
  public disableLoader(): void {
    this.ngRedux.dispatch(UiActions.updateLoaderAction({ loader: false }));
    this.preventLoaderCloseWhenTimeout(false);
  }

  /**
   * Dispatch action for enabling loader
   *
   */
  public enableModalLoader(): void {
    this.ngRedux.dispatch(UiActions.updateModalLoaderAction(true));
  }

  /**
   * Dispatch action for disabling loader
   */
  public disableModalLoader(): void {
    this.ngRedux.dispatch(UiActions.updateModalLoaderAction(false));
  }

  /**
   * Dispatch action for formBuildingStarted
   */
  public formBuildingStarted(options?): void {
    this.ngRedux.dispatch(UiActions.formBuildingStartedAction(options));
  }

  /**
   * Dispatch action for formBuildingComplete
   */
  public formBuildingComplete(options?): void {
    this.ngRedux.dispatch(UiActions.formBuildingCompleteAction(options));
  }

  public saveNonPatchedFormValue(formValue?): void {
    this.ngRedux.dispatch(UiActions.saveNonPatchedFormValueAction(formValue));
  }

  public clearNonPatchedFormValue(): void {
    this.ngRedux.dispatch(UiActions.clearNonPatchedFormValueAction());
  }

  /**
   * Confirmation Modal dialog action dispatcher
   * Completes provided options with all necessary fields to open generic Confirm dialog
   *
   * @returns Promise<IModalDialogResult>
   */
  public confirm(
    userOptions: IModalDialogOptions | string,
  ): Promise<IModalDialogResult> {
    const options = Object.assign(
      {},
      MODAL_TEMPLATES.MODAL_TEMPLATE_CONFIRM,
      typeof userOptions === 'string' ? { text: userOptions } : userOptions,
    );

    return this.formModalDialog(options);
  }

  /**
   * NOTE: this method uses FormModalDialog ("new modal")
   * Prompt Modal dialog action dispatcher
   * Provided default options with all values necessary for generic Prompt dialog.
   *
   * @returns Promise<IModalDialogResult>
   */
  public promptFormModal(
    userOptions: IModalDialogOptions,
  ): Promise<IModalDialogResult> {
    const options = Object.assign(
      {},
      MODAL_TEMPLATES.MODAL_TEMPLATE_PROMPT,
      userOptions,
    );

    return this.formModalDialog(options);
  }

  /**
   * NOTE: this method uses GenericModalComponent ("old modal")
   * Prompt Modal dialog action dispatcher
   * Provided default options with all values necessary for generic Prompt dialog.
   *
   * @returns Promise<IModalDialogResult>
   */
  public prompt(userOptions: IModalDialogOptions): Promise<IModalDialogResult> {
    const options = Object.assign(
      {},
      MODAL_TEMPLATES.MODAL_TEMPLATE_PROMPT,
      userOptions,
    );

    return this.modalDialog(options);
  }

  public async openSiteSelectorModal(
    sites: ISite[],
    selectedSitesIds: string[],
    required: boolean = false,
    hideBackButton = false,
  ): Promise<IModalDialogResult> {
    const minItems = required ? 1 : null;
    const maxItems = Number.MAX_SAFE_INTEGER;
    const modalOptions: IModalDialogOptions = merge(
      {},
      MODAL_TEMPLATES.MODAL_TEMPLATE_SITE_SELECTOR,
      {
        validation: required || undefined,
        value: {
          sites: selectedSitesIds,
          site: head(selectedSitesIds),
        },
        formRules: {
          fields: {
            sites: {
              minItems,
              maxItems,
              title: '',
              widgetData: {
                multiselect: true,
                returnFullData: false,
                hideActionPanel: true,
                valueField: 'store_id',
                showSearch: false,
                gridHeight: '300px',
                selectOptions: sites.map((site) => ({
                  name: site.name,
                  store_id: site.id,
                })),
              },
            },
          },
        },
      },
    );

    if (!required) {
      modalOptions.buttons.push(BUTTON_TEMPLATES.BTN_CANCEL);
    }

    if (hideBackButton || !required) {
      modalOptions.buttons = filter(
        modalOptions.buttons,
        (button) => button.value !== BUTTON_TEMPLATES.BTN_BACK.value,
      );
    }

    const dialogResult = await this.promptFormModal(modalOptions);

    return {
      confirmed: dialogResult.confirmed,
      modalResult: dialogResult.modalResult,
      value: {
        sites: get(dialogResult, 'value.sites', []),
        site: get(dialogResult, 'value.sites.0'),
      },
    };
  }

  public async promptSiteSelector(
    options?: PromptSiteSelectorOptions,
  ): Promise<IModalDialogResult> {
    const state: IAppState = this.ngRedux.getState();
    const sitesCount = get(state, STATE_KEYS.STORES_TOTAL);
    const sites: string[] = get(state, STATE_KEYS.SELECTED_STORES, []);
    const required = get(options, 'required', false);
    const disableHistoryBack = get(options, 'disableHistoryBack', false);
    const hideBackButton = get(options, 'hideBackButton', false);

    if (required && !sitesCount) {
      this.showErrorPage(ERROR_PAGE_TEMPLATES.USER_DOES_NOT_HAVE_SITES);

      return;
    }

    const isIframeMode = await this.ngRedux
      .select(['userSession', 'isIframeMode'])
      .pipe(rx.first())
      .toPromise();
    let result;

    if (isIframeMode) {
      const selectedStores = await this.ngRedux
        .select(STATE_KEYS.SELECTED_STORES)
        .pipe(
          rx.filter((stores) => !isEmpty(stores)),
          rx.first(),
        )
        .toPromise();

      result = {
        confirmed: true,
        modalResult: 'ok',
        value: {
          site: selectedStores[0],
          sites: selectedStores,
        },
      };
    } else {
      const storesList = (await this.ngRedux
        .select(STATE_KEYS.STORES_LIST)
        .pipe(rx.first())
        .toPromise()) as ISite[];
      const dialogResult = await this.openSiteSelectorModal(
        storesList,
        sites,
        required,
        hideBackButton,
      );
      const selectedSites = get(dialogResult, 'value.sites', []);
      result = {
        confirmed: dialogResult.confirmed,
        modalResult: dialogResult.modalResult,
        value: {
          sites: selectedSites,
          site: head(selectedSites),
        },
      };
    }

    if (
      !result.confirmed &&
      result.modalResult === BUTTON_TEMPLATES.BTN_BACK.value &&
      !disableHistoryBack
    ) {
      this.historyBack();
    }

    if (isEqual(result.value.sites, sites)) return;

    return result;
  }

  /**
   * modal dialog action dispatcher.
   * Returns promise for modal dialog result
   */
  public modalDialog(
    options: IModalDialogOptions,
  ): Promise<IModalDialogResult> {
    // force close of currently open modal`
    if (this.ngRedux.getState().ui.genericModalVisible) {
      this.closeGenericModal({ modalResult: 'none' });
    }
    this.ngRedux.dispatch(UiActions.showGenericModalDialogAction(options));

    return this.ngRedux
      .select(['ui', 'genericModalResult'])
      .pipe(rx.find(Boolean))
      .toPromise();
  }

  /** close generic modal action dispatcher */
  public closeGenericModal(result: IModalDialogResult): void {
    this.ngRedux.dispatch(UiActions.hideGenericModalDialogAction(result));
  }

  public formModalDialog(
    incomingOptions: IModalDialogOptions,
  ): Promise<IModalDialogResult> {
    const options = Object.assign({}, incomingOptions);
    const modalId = get(options, 'modalId', null);
    /**
     * check on existing of modalId, every form modal instance should hav a specified modalId
     * the modalId is used for managing modals state in a multimodal mode
     */
    if (!modalId) {
      set(options, 'modalId', generateObjectId());
    }

    const translatedOptions =
      this.i18nService.translateTextInModalDialogOptions(options);

    this.dropDownMenuService.clearContainer();
    this.ngRedux.dispatch(UiActions.showFormModalAction(translatedOptions));

    return this.ngRedux
      .select(STATE_KEYS.FORM_MODAL_RESULT)
      .pipe(
        rx.find((value): boolean => {
          const modalIdFromState = get(value, 'modalId');

          return modalIdFromState === translatedOptions.modalId;
        }),
      )
      .toPromise();
  }

  public closeFormModalDialog(options?: IModalDialogResult): void {
    this.dropDownMenuService.clearContainer();
    this.ngRedux.dispatch(UiActions.hideFormModalAction(options));
  }

  public storeValueByModalId(modalId: string | number, value: any): void {
    this.ngRedux.dispatch(UiActions.storeValueByModalId(modalId, value));
  }

  /**
   * Information Modal dialog action dispatcher
   * Completes provided options with all necessary fields to open generic Alert dialog
   *
   * @returns Promise<IModalDialogResult>
   */
  public alert(
    userOptions: IModalDialogOptions | string,
  ): Promise<IModalDialogResult> {
    const options = Object.assign(
      {},
      MODAL_TEMPLATES.MODAL_TEMPLATE_ALERT,
      typeof userOptions === 'string' ? { text: userOptions } : userOptions,
    );

    return this.formModalDialog(options);
  }

  public showDropdownMenu(options: IDropdownMenuOptions): Promise<any> {
    this.ngRedux.dispatch(UiActions.showDropdownMenuAction(options));

    return this.ngRedux
      .select(['ui', 'dropdownMenuResult'])
      .pipe(rx.find(Boolean))
      .toPromise();
  }

  public hideDropdownMenu(payload: any = null): void {
    this.ngRedux.dispatch(UiActions.hideDropdownMenuAction(payload));
  }

  public showErrorToasterMessage(payload: any = null): void {
    const notification = isString(payload)
      ? {
          // eslint-disable-next-line
          title: payload,
          // eslint-disable-next-line
          type: 'error',
          // eslint-disable-next-line
        }
      : payload;
    this.ngRedux.dispatch(
      UiActions.showErrorToasterMessageAction(notification),
    );
  }

  public showToasterMessage(message: Toast | string): void {
    const toast = Object.assign(
      { type: 'info', toastId: generateId() },
      typeof message === 'string' ? { body: message } : message,
    );
    this.ngRedux.dispatch(UiActions.showToasterMessageAction(toast));
  }

  public showErrorPage(options: string | IErrorPageType): void {
    const newOptions = Object.assign(
      {},
      typeof options === 'string' ? { text: options } : options,
    );
    this.ngRedux.dispatch(UiActions.showErrorPageAction(newOptions));
  }

  /**
   * Method for dispatching openRouteAction action for redirecting to current route
   * TODO: rework this once we upgrade angular to 5-6 version,
   * which have functionality to handle this in more elegant way
   *
   * @param uri: string
   * @param options: object
   */
  public openRoute(uri: string, options: IOpenRouteOptions = {}): void {
    this.ngRedux.dispatch(UiActions.openRouteAction(uri, options));
  }

  public clearDuplicateOption(): void {
    this.ngRedux.dispatch(UiActions.clearDuplicateOption());
  }

  /**
   * Method for dispatching openRouteAction action for redirecting to previous route
   */
  public historyBack(options: any = {}): any {
    const dashboardPath = 'dashboard';
    const history = get(this.ngRedux.getState(), 'tools.history', []);

    this.ngRedux.dispatch(
      UiActions.openRouteAction(get(history, '[1]', dashboardPath), options),
    );
  }

  /**
   * Method for dispatching storeEntityListFilterTextAction action
   *
   * @param text: string
   * @param listFilterText: string - filter text for entity list string
   */
  public storeEntityListFilterText(entityType: string, text: string): void {
    this.ngRedux.dispatch(
      UiActions.storeEntityListFilterTextAction(entityType, text),
    );
  }

  public restoreEntityListView(entityType: string): void {
    this.ngRedux.dispatch(UiActions.restoreEntityListViewAction(entityType));
  }

  public restoreDefaultListView(entityType: string): void {
    this.ngRedux.dispatch(UiActions.restoreDefaultListViewAction(entityType));
  }

  public saveUserListView(viewName: string): void {
    this.ngRedux.dispatch(UiActions.saveUserListViewAction(viewName));
  }

  public removeUserListView(viewName: string): void {
    this.ngRedux.dispatch(UiActions.removeUserListViewAction(viewName));
  }

  public restoreUserListView(viewName: string): void {
    this.ngRedux.dispatch(UiActions.restoreUserListViewAction(viewName));
  }

  public updateListViewData(data: IUIListState): void {
    this.ngRedux.dispatch(UiActions.updateListViewDataAction(data));
  }

  public updateDefaultListViewData(data: IUIListState): void {
    this.ngRedux.dispatch(UiActions.updateDefaultListViewDataAction(data));
  }

  public setCurrentPage(pageNumber: number): void {
    this.ngRedux.dispatch(UiActions.setCurrentPageAction(pageNumber));
  }

  public setUiTheme(theme: string = ''): void {
    this.ngRedux.dispatch(UiActions.setUiThemeAction(theme));
  }

  public setIsDeleteViewMode(flag: boolean = false): void {
    this.ngRedux.dispatch(UiActions.setIsDeleteViewMode(flag));
  }

  public async clipboardWrite(text: string): Promise<boolean> {
    try {
      await (navigator as any).clipboard.writeText(text);

      return true;
    } catch (err) {
      this.showToasterMessage(TOASTER_MESSAGES.CLIPBOARD_ACCESS_WARNING);

      return false;
    }
  }

  public async clipboardRead(): Promise<string> {
    try {
      return await (navigator as any).clipboard.readText();
    } catch (err) {
      this.showToasterMessage(TOASTER_MESSAGES.CLIPBOARD_ACCESS_WARNING);

      return null;
    }
  }

  public setSidebarOpenState(item: IMenuItem, state: boolean): void {
    this.ngRedux.dispatch(UiActions.setSidebarOpenStateAction(item, state));
  }

  public setUiFeatures(features: { [key: string]: boolean }): void {
    this.ngRedux.dispatch(UiActions.setUiFeaturesAction(features));
  }

  public addNotification(notification: INotificationBoxItem): void {
    if (!has(notification, 'showAfter$')) {
      this.addNotificationBase(notification);

      return;
    }
    notification.showAfter$.subscribe({
      complete: (): void => this.addNotificationBase(notification),
    });
  }

  public addNotificationBase(notification: INotificationBoxItem): void {
    this.ngRedux.dispatch(UiActions.addNotificationAction(notification));
    if (has(notification, 'ttl')) {
      setTimeout(
        () =>
          this.ngRedux.dispatch(
            UiActions.removeNotificationAction(notification.id),
          ),
        notification.ttl,
      );
    }
  }

  public removeNotification(id: string): void {
    this.ngRedux.dispatch(UiActions.removeNotificationAction(id));
  }

  public removeAllNotifications(): void {
    this.ngRedux.dispatch(UiActions.removeAllNotificationAction());
  }

  public removeNotificationsOnPageLeave(): void {
    this.ngRedux.dispatch(UiActions.removeNotificationOnPageLeave());
  }

  public openRouteWithFilterModel({
    url,
    entityType,
    filterModel,
  }: IOpenRouteWithFilterModelType): void {
    this.updateListViewData({
      entityType,
      filterModel,
    });
    this.openRoute(url);
  }

  public setGridRowLoader(isActiveState: boolean = false): void {
    this.ngRedux.dispatch(UiActions.setGridRowLoaderAction(isActiveState));
  }

  // can prevent loader auto close when loading over than 35s.
  public preventLoaderCloseWhenTimeout(isLoading: boolean = false): void {
    this.ngRedux.dispatch(UiActions.setBatchRequestLoadingStatus(isLoading));
  }

  public reloadListConfig(changesNumber: number = 0): void {
    this.ngRedux.dispatch(UiActions.reloadListConfig(changesNumber));
  }

  public preventDisableLoaderAfterRequestFinish(
    isPreventLoaderCloseAfterRequestFinish: boolean = false,
  ): void {
    this.ngRedux.dispatch(
      UiActions.updatePreventDisableLoaderStatus(
        isPreventLoaderCloseAfterRequestFinish,
      ),
    );
  }

  public showCartErrorToasterMessage(payload: INotificationToaster): void {
    this.ngRedux.dispatch(UiActions.showCartErrorToasterMessageAction(payload));
  }

  public updateBreadcrumbButtons(payload: ToolbarButtonDefinition[]): void {
    this.ngRedux.dispatch(UiActions.updateBreadcrumbButtons(payload));
  }

  public updateUserConfig(userConfigs: IUserConfig[]): void {
    this.ngRedux.dispatch(UiActions.updateUserConfigAction(userConfigs));
  }

  // eslint-disable-next-line max-lines
}
